import { definePreset } from "@primeng/themes";
import type { ComponentsDesignTokens } from "@primeuix/themes/types";
import Aura from '@primeng/themes/aura';
import Lara from '@primeng/themes/lara';
import Material from '@primeng/themes/material';
import Nora from '@primeng/themes/nora';
import componentTokens from "./template.PrimeNG.component.tokens";
import TemplateNoraPreset from "./template.PrimeNG.Nora.preset";
/*
    Use PrimeNGs token based theming system.

    The tokens set in here will end up in <style> tags in the resulting html. 
    Each token sets a variable (like p-button-primary-background-color)

    You can easily override these in compnent scss files by providing a
    selector with a higher specificity. The preferred method is to set the
    corresponding variable if it exists ( as opposed to setting property directly ).
    
    To set a specific property, try in this order:
      1 Override the --custom- variable if it exists
      2 Override the --p- variable if it exists
      3 Create rule based on the classes as usual

    For a new component type do this:
    - Add a section to the componentTokens and set the tokens as appropriate
    - If possible refer to a --custom- variable if it exists, otherwise a semanticToken that already exists or create a new one
    - For semantic tokens, if possible, refer to an existing primitive token

    You can specify selectors several ways:
    - prop: { subprop: { nothersubprop: value} }
    - "prop.subprop.nothersubprop": value     // prop name must be in quotes because of the periods
    - propSubpropNothersubprop: value         // quotes not needed with camelCase

    I prefer the dot notation because you can copy and paste from the PrimeNG page for the component

*/

const primitiveTokens
//: PrimitiveDesignTokens
= {
    blue: {

        // These were defined by Gregg
        50: "var(--Color__Blue__Blue-50)",
        100: "var(--Color__Blue__Blue-100)",
        200: "var(--Color__Blue__Blue-200)",
        300: "var(--Color__Blue__Blue-300)",
        400: "var(--Color__Blue__Blue-400)",
        500: "var(--Color__Blue__Blue-500)",
        600: "var(--Color__Blue__Blue-600)",
        650: "var(--Color__Blue__Blue-650)", // latest color
        700: "var(--Color__Blue__Blue-700)",

        // These are PrimeNG defaults
        800: "#1e40af",
        900: "#1e3a8a",
        950: "#172554",
    },

    orange: {
        20: "var(--Color__Orange__Orange-20)",
        50: "var(--Color__Orange__Orange-50)"
    }
}

// Semantic tokens map to primitive tokens or other semantic tokens. 
// Not sure if these are actually used anywhere.
const semanticTokens
= {
    primary: {
        50: '{blue.50}',
        100: '{blue.100}',
        200: '{blue.200}',
        300: '{blue.300}',
        400: '{blue.400}',
        500: '{blue.500}',
        600: '{blue.600}',
        650: '{blue.650}',
        700: '{blue.700}',
        800: '{blue.800}',
        900: '{blue.900}',
    //    green: "green"

    },
    focusRing: {
        width: '2px',
        style: 'dashed',
        color: '{primary.color}',
        offset: '1px'
    },
    
    // The colorScheme token group is a special variable to define tokens based on the color scheme active in the application, 
    // this allows defining different tokens based on the color scheme like dark mode.
    // colorScheme: {
    //     light: {
    //         primary: {
    //             color: '{primary.900}',
    //             contrastColor: '#ffffff',
    //             hoverColor: '{primary.600}',
    //             activeColor: '{primary.700}'
    //         },
    //         highlight: {
    //             background: '{primary.50}',
    //             focusBackground: '{primary.100}',
    //             color: '{primary.700}',
    //             focusColor: '{primary.800}'
    //         },
    //         mask: {
    //             background: 'rgba(0,0,0,0.4)',
    //             color: '{surface.200}'
    //         },
    //         formField: {
    //             hoverbordercolor: '{primary.color}'
    //         },
    //         surface: {
    //             0: '#ffffff',
    //             50: '{zinc.50}',
    //             100: '{zinc.100}',
    //             200: '{zinc.200}',
    //             300: '{zinc.300}',
    //             400: '{zinc.400}',
    //             500: '{zinc.500}',
    //             600: '{zinc.600}',
    //             700: '{zinc.700}',
    //             800: '{zinc.800}',
    //             900: '{zinc.900}',
    //             950: '{zinc.950}'
    //         }
    //     },
    //     dark: {
    //         formField: {
    //             hoverBorderColor: '{primary.color}'
    //         },
    //         surface: {
    //             0: '#ffffff',
    //             50: '{slate.50}',
    //             100: '{slate.100}',
    //             200: '{slate.200}',
    //             300: '{slate.300}',
    //             400: '{slate.400}',
    //             500: '{slate.500}',
    //             600: '{slate.600}',
    //             700: '{slate.700}',
    //             800: '{slate.800}',
    //             900: '{slate.900}',
    //             950: '{slate.950}'
    //         }
    //     }
    // }       
};

/**
 * Component tokens are isolated tokens per component such as inputtext.background or button.color that map to the semantic tokens.
 * As an example, button.background component token maps to the primary.color semantic token which maps to the green.500 primitive token. 
 */
// const componentTokens
// //: ComponentsDesignTokens // v19, not v18 :(
// = {
//   // p-button
//   button: {
//     root: {
//       primary: {
//         //background: "var(--custom-button-background-color)"             // This does not work
//         background: "var(--custom-button-background-color) !important"  // This DOES work
//       }
//     }
//   },

//     // p-checkbox (purposely showing different ways of specifying selectors)
//     checkbox: {
//         checked:{
//             background: 'var(--custom-input-checkbox-checked-background-color)',
//             hover: { background: 'var(--custom-input-checkbox-checked-background-color)' },
//             "border.color":'var(--custom-input-checkbox-checked-background-color)',
//             "hover.border.color": "var(--custom-input-checkbox-checked-background-color)"
//         },
//         "border.color": "var(--custom-input-checkbox-border-color)",
//         hoverBorderColor: "var(--custom-input-checkbox-border-color)"

//     },
//     // treetable
//     treetable: {
//         'body.cell.padding': '0',
//         'node.toggle.button.size': '32px' 
//     },

//      // p-card
//     card: {
//         colorScheme: {
//             light: {
//                 root: {
//                     background: '{surface.0}',
//                     color: '{surface.700}'
//                 },
//                 subtitle: {
//                     color: '{surface.500}'
//                 }
//             },
//             dark: {
//                 root: {
//                     background: '{surface.900}',
//                     color: '{surface.0}'
//                 },
//                 subtitle: {
//                     color: '{surface.400}'
//                 }
//             }
//         }
//     }
// };

const templateComponentTokens = componentTokens as unknown as ComponentsDesignTokens;

const TemplateThemePreset = definePreset(
    //Aura,
    //Lara,
    //Material,
    TemplateNoraPreset,   // No weird border on user menu, tabs are more tabby by default
    //Nora,
    {    
        primitives: primitiveTokens,
        semantic: semanticTokens,
        components: templateComponentTokens
    }
);

export { TemplateThemePreset };


